import React, { useState, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { usePermissions } from "../../rbac/PermissionsContext";
import {
  Cake,
  Gift,
  UserPlus,
  CalendarDays,
  Clock3,
  BellRing,
  ArrowRight,
} from "lucide-react";

const Home = memo(
  function Home({ darkMode, setCurrentPage, toggleDarkMode }) {
    const [companyData, setCompanyData] = useState(null);
    const { hasScreenAccess, isAdmin } = usePermissions();

    const canSeeBirthdays = isAdmin || hasScreenAccess("home", "birthdays", "view");
    const canSeeAnniversaries = isAdmin || hasScreenAccess("home", "work_anniversaries", "view");
    const canSeeNewJoinees = isAdmin || hasScreenAccess("home", "new_joinees", "view");
    const canSeeGlance = isAdmin || hasScreenAccess("home", "glance", "view");

    useEffect(() => {
      const companyData = JSON.parse(localStorage.getItem("companyData"));
      setCompanyData(companyData);
    }, []);

    const birthdayList = [
      { name: "Kranthi", team: "Engineering", date: "Today", note: "Backend Team", initials: "KR" },
      { name: "Nandhini", team: "People Ops", date: "Tomorrow", note: "Culture Committee", initials: "NA" },
      { name: "Rohit S", team: "Finance", date: "In 3 days", note: "Payroll Owner", initials: "RS" },
    ];

    const anniversaryList = [
      { name: "Arun Prakash", years: 4, date: "Today", team: "Engineering", initials: "AP" },
      { name: "Priya S", years: 2, date: "In 2 days", team: "Operations", initials: "PS" },
      { name: "Anita M", years: 6, date: "This week", team: "People Ops", initials: "AM" },
    ];

    const newJoinees = [
      { name: "Madhan Kumar", role: "Backend Developer", joining: "Today", buddy: "Kranthi", initials: "MK" },
      { name: "Shalini R", role: "HR Executive", joining: "Next Monday", buddy: "Priya S", initials: "SR" },
      { name: "Vignesh T", role: "QA Engineer", joining: "Next Wednesday", buddy: "Arun P", initials: "VT" },
    ];

    const quickActions = [
      { label: "Employee Directory", page: "Employee" },
      { label: "Attendance", page: "Attendance" },
      { label: "Payroll", page: "Payroll" },
      { label: "User Roles", page: "User Roles" },
    ];

    return (
      <div className="p-6">
        <div className="mx-auto max-w-8xl h-full bg-white rounded-lg shadow-md overflow-hidden p-6 space-y-6">
            <div
              className={`rounded-2xl border p-5 ${
                darkMode ? "border-[#3C3C4C] bg-[#1D1D1D]" : "border-gray-200 bg-white"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {companyData?.logo ? (
                    <img
                      src={companyData.logo}
                      alt={`${companyData.name} logo`}
                      className="h-12 w-12 rounded-md object-contain"
                    />
                  ) : (
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-md ${
                        darkMode ? "bg-[#2a2a2a]" : "bg-redhalo-gray/40"
                      }`}
                    >
                      <BellRing className="h-6 w-6 text-redhalo-red" />
                    </div>
                  )}
                  <div>
                    <h1 className={`text-2xl font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
                      Welcome back to PaceHRM
                    </h1>
                    <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
                      Stay updated with celebrations, onboarding, and day-to-day people actions.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {quickActions.map((action) => (
                    <button
                      key={action.label}
                      type="button"
                      onClick={() => setCurrentPage(action.page)}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        darkMode
                          ? "border-[#3C3C4C] bg-[#2a2a2a] text-gray-200 hover:border-redhalo-mint hover:text-redhalo-mint"
                          : "border-gray-200 bg-white text-gray-700 hover:border-redhalo-red hover:text-redhalo-red"
                      }`}
                    >
                      {action.label}
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
              {canSeeBirthdays && (
              <div className={`rounded-2xl border p-5 ${darkMode ? "border-[#3C3C4C] bg-[#1D1D1D]" : "border-gray-200 bg-white"}`}>
                <div className="mb-3 flex items-center gap-2">
                  <Cake className="h-5 w-5 text-redhalo-red" />
                  <h2 className={`text-base font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>Birthdays</h2>
                </div>
                <div className="space-y-3">
                  {birthdayList.map((person) => (
                    <div key={person.name} className={`rounded-xl border p-3 ${darkMode ? "border-[#3C3C4C] bg-[#232323]" : "border-gray-100 bg-gray-50"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2">
                          <div className={`flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold ${
                            darkMode ? "bg-[#2f2f2f] text-redhalo-mint" : "bg-redhalo-gray/50 text-redhalo-red"
                          }`}>
                            {person.initials}
                          </div>
                          <div>
                            <p className={`text-sm font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>{person.name}</p>
                            <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>{person.team} - {person.date}</p>
                            <p className={`text-[11px] mt-0.5 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>{person.note}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage("Employee")}
                          className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                            darkMode
                              ? "bg-redhalo-red/20 text-redhalo-mint hover:bg-redhalo-red/30"
                              : "bg-redhalo-red/10 text-redhalo-red hover:bg-redhalo-red/20"
                          }`}
                        >
                          Wish
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {canSeeAnniversaries && (
              <div className={`rounded-2xl border p-5 ${darkMode ? "border-[#3C3C4C] bg-[#1D1D1D]" : "border-gray-200 bg-white"}`}>
                <div className="mb-3 flex items-center gap-2">
                  <Gift className="h-5 w-5 text-redhalo-red" />
                  <h2 className={`text-base font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>Work Anniversaries</h2>
                </div>
                <div className="space-y-3">
                  {anniversaryList.map((person) => (
                    <div key={person.name} className={`rounded-xl border p-3 ${darkMode ? "border-[#3C3C4C] bg-[#232323]" : "border-gray-100 bg-gray-50"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className={`text-sm font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>{person.name}</p>
                          <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>{person.years} years - {person.date}</p>
                          <p className={`text-[11px] mt-0.5 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>{person.team}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage("Employee")}
                          className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                            darkMode
                              ? "bg-redhalo-red/20 text-redhalo-mint hover:bg-redhalo-red/30"
                              : "bg-redhalo-red/10 text-redhalo-red hover:bg-redhalo-red/20"
                          }`}
                        >
                          Celebrate
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {canSeeNewJoinees && (
              <div className={`rounded-2xl border p-5 ${darkMode ? "border-[#3C3C4C] bg-[#1D1D1D]" : "border-gray-200 bg-white"}`}>
                <div className="mb-3 flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-redhalo-red" />
                  <h2 className={`text-base font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>New Joinees</h2>
                </div>
                <div className="space-y-3">
                  {newJoinees.map((person) => (
                    <div key={person.name} className={`rounded-xl border p-3 ${darkMode ? "border-[#3C3C4C] bg-[#232323]" : "border-gray-100 bg-gray-50"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className={`text-sm font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>{person.name}</p>
                          <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>{person.role} - {person.joining}</p>
                          <p className={`text-[11px] mt-0.5 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>Buddy: {person.buddy}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => setCurrentPage("Employee")}
                            className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                              darkMode
                                ? "bg-emerald-900/40 text-emerald-200 hover:bg-emerald-900/60"
                                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            }`}
                          >
                            Send Welcome
                          </button>
                          <button
                            type="button"
                            onClick={() => setCurrentPage("Employee")}
                            className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                              darkMode
                                ? "bg-[#2a2a2a] text-gray-200 hover:text-redhalo-mint"
                                : "bg-white text-gray-700 hover:text-redhalo-red"
                            }`}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>

            {canSeeGlance && (
            <div className={`rounded-2xl border p-5 ${darkMode ? "border-[#3C3C4C] bg-[#1D1D1D]" : "border-gray-200 bg-white"}`}>
              <div className="mb-3 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-redhalo-red" />
                <h2 className={`text-base font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>Today at a Glance</h2>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {[
                  { title: "Total Employees", value: "128", note: "Active workforce" },
                  { title: "On Leave", value: "07", note: "Approved leaves today" },
                  { title: "Pending Approvals", value: "05", note: "Attendance & requests" },
                ].map((item) => (
                  <div key={item.title} className={`rounded-xl border p-4 ${darkMode ? "border-[#3C3C4C] bg-[#232323]" : "border-gray-100 bg-gray-50"}`}>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{item.title}</p>
                    <p className={`mt-1 text-2xl font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>{item.value}</p>
                    <p className={`mt-1 text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>{item.note}</p>
                  </div>
                ))}
              </div>
              <div className={`mt-4 inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs ${
                darkMode ? "bg-[#2a2a2a] text-gray-300" : "bg-redhalo-gray/40 text-gray-700"
              }`}>
                <Clock3 className="h-4 w-4 text-redhalo-red" />
                Tip: Use quick actions above to jump to attendance, payroll, and employee records.
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage("Employee")}
                  className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                    darkMode
                      ? "bg-redhalo-red/20 text-redhalo-mint hover:bg-redhalo-red/30"
                      : "bg-redhalo-red/10 text-redhalo-red hover:bg-redhalo-red/20"
                  }`}
                >
                  View Team Highlights
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage("Attendance")}
                  className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                    darkMode
                      ? "bg-[#2a2a2a] text-gray-200 hover:text-redhalo-mint"
                      : "bg-white text-gray-700 hover:text-redhalo-red border border-gray-200"
                  }`}
                >
                  Open Attendance Board
                </button>
              </div>
            </div>
            )}
          </div>
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.darkMode === nextProps.darkMode
);

Home.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  toggleDarkMode: PropTypes.func.isRequired,
};

export default Home;
